import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { KeyRound } from 'lucide-react';
import { api, useAuth } from '../hooks/useAuth';
import Button from '../components/Button';
import toast from 'react-hot-toast';

interface FormData {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

const ForceChangePassword: React.FC = () => {
  const { completePasswordChange } = useAuth();
  const navigate = useNavigate();
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<FormData>();

  const newPassword = watch('newPassword');

  const onSubmit = async (data: FormData) => {
    setSubmitting(true);
    try {
      const response = await api.post('/users/reset-password', {
        currentPassword: data.currentPassword,
        newPassword: data.newPassword,
      });
      const { token, user } = response.data as {
        token: string;
        user: {
          id: string;
          email: string;
          firstName: string;
          lastName: string;
          role: string;
          mustChangePassword: boolean;
        };
      };
      completePasswordChange(token, user);
      toast.success('Password updated. You can continue.');
      navigate('/', { replace: true });
    } catch (e: unknown) {
      const err = e as { response?: { data?: { error?: { message?: string } } } };
      const msg = err.response?.data?.error?.message || 'Could not update password';
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="etr-auth-shell">
      <div className="etr-auth-card max-w-md">
        <div className="mx-auto h-12 w-12 flex items-center justify-center rounded-full bg-amber-100">
          <KeyRound className="h-6 w-6 text-amber-700" />
        </div>
        <h2 className="mt-6 text-center text-2xl font-extrabold text-gray-900">Set a new password</h2>
        <p className="mt-2 text-center text-sm text-gray-600">
          Your account was created with a temporary password. Choose a new password to access the app.
        </p>

        <form className="mt-8 space-y-4" onSubmit={handleSubmit(onSubmit)}>
          <div>
            <label className="block text-sm font-medium text-gray-700">Current password</label>
            <div className="relative mt-1">
              <input
                type={showCurrent ? 'text' : 'password'}
                autoComplete="current-password"
                className="etr-input"
                {...register('currentPassword', { required: 'Required' })}
              />
              <button
                type="button"
                className="absolute inset-y-0 right-0 pr-3 flex items-center text-sm text-gray-500"
                onClick={() => setShowCurrent(!showCurrent)}
              >
                {showCurrent ? 'Hide' : 'Show'}
              </button>
            </div>
            {errors.currentPassword && (
              <p className="mt-1 text-sm text-red-600">{errors.currentPassword.message}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">New password</label>
            <div className="relative mt-1">
              <input
                type={showNew ? 'text' : 'password'}
                autoComplete="new-password"
                className="etr-input"
                {...register('newPassword', {
                  required: 'Required',
                  minLength: { value: 8, message: 'At least 8 characters' },
                })}
              />
              <button
                type="button"
                className="absolute inset-y-0 right-0 pr-3 flex items-center text-sm text-gray-500"
                onClick={() => setShowNew(!showNew)}
              >
                {showNew ? 'Hide' : 'Show'}
              </button>
            </div>
            {errors.newPassword && <p className="mt-1 text-sm text-red-600">{errors.newPassword.message}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Confirm new password</label>
            <div className="relative mt-1">
              <input
                type={showConfirm ? 'text' : 'password'}
                autoComplete="new-password"
                className="etr-input"
                {...register('confirmPassword', {
                  required: 'Required',
                  validate: (v) => v === newPassword || 'Passwords do not match',
                })}
              />
              <button
                type="button"
                className="absolute inset-y-0 right-0 pr-3 flex items-center text-sm text-gray-500"
                onClick={() => setShowConfirm(!showConfirm)}
              >
                {showConfirm ? 'Hide' : 'Show'}
              </button>
            </div>
            {errors.confirmPassword && (
              <p className="mt-1 text-sm text-red-600">{errors.confirmPassword.message}</p>
            )}
          </div>

          <Button type="submit" className="w-full" disabled={submitting}>
            {submitting ? 'Saving…' : 'Save new password'}
          </Button>
        </form>
      </div>
    </div>
  );
};

export default ForceChangePassword;
